/**
 * Drag-and-drop scenario definitions and utilities
 * Defines different move scenarios with their metadata, impact items, and styling
 */

export type ScenarioType = 
  | 'uploaded-to-uploaded'
  | 'unknown-to-sorted'
  | 'cross-contract-move'
  | 'same-contract-sorted'
  | 'folder-uploaded-move'
  | 'folder-sorted-move'
  | 'unknown';

export interface ImpactItem {
  icon: string;
  text: string;
}

export interface ScenarioConfig {
  type: ScenarioType;
  title: string;
  icon: string;
  titleColor: string;
  buttonColor: 'danger' | 'primary';
  impactItems: ImpactItem[];
}

export const SCENARIO_CONFIGS: Record<ScenarioType, ScenarioConfig> = {
  'uploaded-to-uploaded': {
    type: 'uploaded-to-uploaded',
    title: 'Updates',
    icon: '📋',
    titleColor: '#666',
    buttonColor: 'primary',
    impactItems: [
      { icon: '', text: 'File location will be updated' },
      { icon: '', text: 'No contract metadata changes (Uploaded section only)' },
      { icon: '', text: 'File movement history will be recorded' },
    ],
  },
  'unknown-to-sorted': {
    type: 'unknown-to-sorted',
    title: 'Contract Metadata Will Update',
    icon: '⚠️',
    titleColor: '#d9534f',
    buttonColor: 'danger',
    impactItems: [
      { icon: '', text: 'File will be assigned to the selected contract' },
      { icon: '', text: 'Party A and Party B will be set based on the destination' },
      { icon: '', text: 'Contract Title will be set' },
      { icon: '', text: 'All document links will be updated' },
      { icon: '', text: 'File movement history will be recorded (including original extracted data)' },
    ],
  },
  'cross-contract-move': {
    type: 'cross-contract-move',
    title: 'Contract Metadata Will Update',
    icon: '⚠️',
    titleColor: '#d9534f',
    buttonColor: 'danger',
    impactItems: [
      { icon: '', text: 'Contract will change to the selected destination contract' },
      { icon: '', text: 'Party A and Party B will be updated' },
      { icon: '', text: 'Contract Title will be updated' },
      { icon: '', text: 'All associated document links will be updated' },
      { icon: '', text: 'File movement history will be recorded (including original extracted data)' },
    ],
  },
  'same-contract-sorted': {
    type: 'same-contract-sorted',
    title: 'File Location Update',
    icon: 'ℹ️',
    titleColor: '#5cb85c',
    buttonColor: 'primary',
    impactItems: [
      { icon: '', text: 'File will be moved to new location within the same contract' },
      { icon: '', text: 'Party A, Party B, and Contract Title remain unchanged' },
      { icon: '', text: 'File movement history will be recorded' },
    ],
  },
  'folder-uploaded-move': {
    type: 'folder-uploaded-move',
    title: 'Updates',
    icon: '📋',
    titleColor: '#666',
    buttonColor: 'primary',
    impactItems: [
      { icon: '', text: 'Folder and everything inside it will move to the new location' },
      { icon: '', text: 'No contract metadata changes (Uploaded section only)' },
      { icon: '', text: 'Folder movement history will be recorded' },
    ],
  },
  'folder-sorted-move': {
    type: 'folder-sorted-move',
    title: 'Contract Structure Will Change',
    icon: '⚠️',
    titleColor: '#d9534f',
    buttonColor: 'danger',
    impactItems: [
      { icon: '', text: 'Folder and every file inside it will move to the destination folder' },
      { icon: '', text: 'Contract Title, Party A and Party B may be updated for those files' },
      { icon: '', text: 'All associated document links will be updated' },
      { icon: '', text: 'Folder movement history will be recorded' },
    ],
  },
  'unknown': {
    type: 'unknown',
    title: 'Move Details',
    icon: '⚠️',
    titleColor: '#f0ad4e',
    buttonColor: 'primary',
    impactItems: [
      { icon: '', text: 'Review details before confirming' },
      { icon: '', text: 'File movement history will be recorded' },
    ],
  },
};

/**
 * Determine which scenario applies based on source/destination buckets and contract info
 *
 * `itemType` matters because a folder move carries its whole subtree with it, so
 * the impact wording differs from a single-file move.
 */
export const determineScenario = (
  sourceBucket: string | null | undefined,
  targetBucket: string | null | undefined,
  sourceContractTitle?: string,
  destContractTitle?: string,
  itemType: 'file' | 'folder' = 'file'
): ScenarioType => {
  const isUploadedBucket = (bucket: string | null | undefined): boolean =>
    bucket === 'unsorted' || bucket === 'files';

  if (itemType === 'folder') {
    if (isUploadedBucket(sourceBucket) && isUploadedBucket(targetBucket)) {
      return 'folder-uploaded-move';
    }
    if (sourceBucket === 'sorted' && targetBucket === 'sorted') {
      return 'folder-sorted-move';
    }
    return 'unknown';
  }

  // Files moving within Uploaded section
  if (isUploadedBucket(sourceBucket) && isUploadedBucket(targetBucket)) {
    return 'uploaded-to-uploaded';
  }

  // Files moving from Unknown to Sorted
  if (sourceBucket === 'unknown' && targetBucket === 'sorted') {
    return 'unknown-to-sorted';
  }

  // Sorted section moves
  if (sourceBucket === 'sorted' && targetBucket === 'sorted') {
    // Check if it's cross-contract or same-contract
    if (sourceContractTitle !== destContractTitle) {
      return 'cross-contract-move';
    }
    return 'same-contract-sorted';
  }

  return 'unknown';
};

/**
 * Get scenario config based on scenario type
 */
export const getScenarioConfig = (scenario: ScenarioType): ScenarioConfig => {
  return SCENARIO_CONFIGS[scenario];
};
